import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, useMotionValueEvent, useScroll } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { mainNav } from '../../data/site';
import { useIntro } from '../../context/IntroContext';
import { cn } from '../../lib/utils';
import { EASE_OUT } from '../../lib/motion';
import AnimatedBackground from '../fx/AnimatedBackground';
import Button from '../primitives/Button';
import Logo from './Logo';
import ServicesMenu from './ServicesMenu';
import MobileMenu from './MobileMenu';
import StatusClock from './StatusClock';
import ThemeToggle from './ThemeToggle';
import { useScrollLock } from './SmoothScroll';

const CLOSE_DELAY = 140;

// Tone of whatever sits under the header bar: sections marked data-tone="ink" flip it dark
const toneUnderHeader = () => {
  const probeY = (document.querySelector('header')?.offsetHeight || 72) / 2;
  const hit = document.elementsFromPoint(window.innerWidth / 2, probeY).find(el => !el.closest('header'));
  return hit?.closest('[data-tone]')?.getAttribute('data-tone') === 'ink' ? 'ink' : 'page';
};

/*
 * Global header. Transparent over the hero, frosted once the page scrolls, tucked away
 * while scrolling down and back on scroll up. Over ink sections it switches to the ink
 * palette (in either theme). A hover pill glides between links.
 * Services opens a mega menu (hover, click or keyboard; Escape closes).
 */
const Header = () => {
  const { pathname } = useLocation();
  const { scrollY } = useScroll();
  const { ready } = useIntro();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tone, setTone] = useState('ink');
  const closeTimer = useRef(null);
  const toneFrame = useRef(0);

  const updateTone = useCallback(() => {
    cancelAnimationFrame(toneFrame.current);
    toneFrame.current = requestAnimationFrame(() => setTone(toneUnderHeader()));
  }, []);

  useMotionValueEvent(scrollY, 'change', latest => {
    const previous = scrollY.getPrevious() ?? 0;
    setScrolled(latest > 24);
    updateTone();
    if (menuOpen || mobileOpen) return;
    if (latest > previous && latest > 240) setHidden(true);
    else if (latest < previous - 4) setHidden(false);
  });

  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setMobileOpen(false);
  }, []);

  useEffect(() => {
    closeAll();
    setHidden(false);
    // Re-check once the route transition has uncovered the new page
    updateTone();
    const id = setTimeout(updateTone, 900);
    window.addEventListener('resize', updateTone);
    return () => {
      clearTimeout(id);
      window.removeEventListener('resize', updateTone);
    };
  }, [pathname, closeAll, updateTone]);

  // The preloader covers the page on a first visit; re-read the tone once it has lifted
  useEffect(() => {
    if (!ready) return undefined;
    const id = setTimeout(updateTone, 850);
    return () => clearTimeout(id);
  }, [ready, updateTone]);

  useEffect(() => {
    const onKey = e => e.key === 'Escape' && closeAll();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeAll]);

  // Hold the page still behind the mobile menu
  useScrollLock(mobileOpen);

  useEffect(() => () => {
    clearTimeout(closeTimer.current);
    cancelAnimationFrame(toneFrame.current);
  }, []);

  const openMenu = () => {
    clearTimeout(closeTimer.current);
    setMenuOpen(true);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenuOpen(false), CLOSE_DELAY);
  };

  const onServicesPage = ['/salesforce', '/erp', '/consultancy', '/ai-agents'].includes(pathname);
  const linkClass = active =>
    cn('px-3.5 py-2 text-[0.875rem] transition-colors duration-300', active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground');

  return (
    <>
      <motion.header
        className={cn('fixed inset-x-0 top-0 z-50', tone === 'ink' && !mobileOpen && 'theme-ink')}
        animate={{ y: hidden ? '-100%' : '0%' }}
        transition={{ duration: 0.55, ease: EASE_OUT }}
      >
        <div
          className={cn(
            'relative z-10 border-b transition-[background-color,border-color,backdrop-filter] duration-500',
            scrolled || menuOpen
              ? 'border-border/70 bg-background/70 backdrop-blur-xl backdrop-saturate-150'
              : 'border-transparent bg-transparent'
          )}
        >
          <div className="container-site flex h-[var(--header-h)] items-center justify-between gap-6">
            <Logo onClick={closeAll} onInk={tone === 'ink' && !mobileOpen} />

            <nav aria-label="Main" className="hidden lg:block">
              <ul className="flex items-center">
                <AnimatedBackground enableHover className="rounded-full bg-foreground/[0.06]" transition={{ type: 'spring', bounce: 0, duration: 0.35 }}>
                  <li data-id="services" onMouseEnter={openMenu} onMouseLeave={scheduleClose}>
                    <button
                      type="button"
                      className={linkClass(onServicesPage || menuOpen)}
                      aria-expanded={menuOpen}
                      aria-controls="services-menu"
                      onClick={() => setMenuOpen(open => !open)}
                    >
                      <span className="inline-flex items-center gap-1">
                        Services
                        <ChevronDown size={14} aria-hidden="true" className={cn('transition-transform duration-300', menuOpen && 'rotate-180')} />
                      </span>
                    </button>
                  </li>
                  {mainNav.map(item => (
                    <li key={item.path} data-id={item.path}>
                      <NavLink to={item.path} className={({ isActive }) => linkClass(isActive)}>
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </AnimatedBackground>
              </ul>
            </nav>

            <div className="flex items-center gap-2">
              <StatusClock className="mr-3 hidden xl:inline-flex" />
              <ThemeToggle className="hidden lg:inline-flex" />
              <Button to="/contact" size="sm" className="hidden sm:inline-flex">Book a consultation</Button>
              <button
                type="button"
                className="relative inline-flex size-10 items-center justify-center rounded-full lg:hidden"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
                aria-controls="mobile-menu"
                onClick={() => setMobileOpen(open => !open)}
              >
                <span className="relative block h-3 w-5" aria-hidden="true">
                  <span className={cn('absolute left-0 top-0 h-px w-full bg-foreground transition-transform duration-500 ease-out-expo', mobileOpen && 'translate-y-1.5 rotate-45')} />
                  <span className={cn('absolute bottom-0 left-0 h-px w-full bg-foreground transition-transform duration-500 ease-out-expo', mobileOpen && '-translate-y-1.5 -rotate-45')} />
                </span>
              </button>
            </div>
          </div>
        </div>

        <ServicesMenu open={menuOpen} onEnter={openMenu} onLeave={scheduleClose} onNavigate={closeAll} />
      </motion.header>
      {/* Outside the header: its transform would otherwise trap this fixed overlay */}
      <MobileMenu open={mobileOpen} onNavigate={closeAll} />
    </>
  );
};

export default Header;
